import { useState } from 'react';
import { Accordion, Badge, Button, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useTranslation } from 'react-i18next';

import { listAuditLogs } from '../../api/resources';
import { CopyableId } from '../../components/CopyableText';
import { DataPanel } from '../../components/DataPanel';
import { DetailField, DetailFieldGrid } from '../../components/detail/DetailField';
import { EntityRef } from '../../components/EntityRef';
import { JsonBlock } from '../../components/JsonBlock';
import { PageHeader } from '../../components/PageHeader';
import { PaginatedTable } from '../../components/PaginatedTable';
import { useAdminLookups } from '../../hooks/useAdminLookups';
import { entityTypeLabel } from '../../lib/labels';
import { DEFAULT_PAGE_SIZE, useListQuery } from '../../hooks/useListQuery';
import { AUDIT_ACTIONS, AUDIT_ENTITY_TYPES, type AuditLog } from '../../types/api';

function entityDetailPath(log: AuditLog): string | null {
    switch (log.entityType) {
        case 'case':
            return `/cases/${log.entityId}`;
        case 'visit':
            return `/visits/${log.entityId}`;
        case 'report':
            return `/reports?id=${log.entityId}`;
        case 'organization':
            return `/organizations?id=${log.entityId}`;
        default:
            return null;
    }
}

export function AuditLogsPage() {
    const { t } = useTranslation();
    const lookups = useAdminLookups({ includeCases: true, includeVisits: true });
    const [page, setPage] = useState(1);
    const [entityType, setEntityType] = useState<string | null>(null);
    const [entityId, setEntityId] = useState('');
    const [action, setAction] = useState<string | null>(null);
    const [performedBy, setPerformedBy] = useState('');
    const [searchQ, setSearchQ] = useState('');
    const [organizationId, setOrganizationId] = useState<string | null>(null);
    const [from, setFrom] = useState<string | null>(null);
    const [to, setTo] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const { data, isLoading, refetch } = useListQuery('audit-logs', listAuditLogs, {
        page,
        entityType: entityType ?? undefined,
        entityId: entityId || undefined,
        action: action ?? undefined,
        performedBy: performedBy || undefined,
        q: searchQ || undefined,
        organizationId: organizationId ?? undefined,
        from: from ?? undefined,
        to: to ?? undefined,
    });

    const expandedLog = (data?.data ?? []).find((r) => r.id === expandedId);

    return (
        <Stack gap="lg">
            <PageHeader
                title={t('features.audit.title')}
                description={t('features.audit.description')}
            />
            <DataPanel>
                <Stack gap="md">
                    <Group align="flex-end" grow>
                        <Select
                            label={t('fields.entityType')}
                            clearable
                            searchable
                            data={AUDIT_ENTITY_TYPES.map((type) => ({
                                value: type,
                                label: entityTypeLabel(type),
                            }))}
                            value={entityType}
                            onChange={setEntityType}
                        />
                        <TextInput
                            label={t('fields.entityId')}
                            placeholder={t('common.placeholders.uuid')}
                            value={entityId}
                            onChange={(e) => setEntityId(e.currentTarget.value)}
                        />
                        <Select
                            label={t('fields.action')}
                            clearable
                            data={AUDIT_ACTIONS.map((a) => ({ value: a, label: a }))}
                            value={action}
                            onChange={setAction}
                        />
                        <Select
                            label={t('fields.performedBy')}
                            placeholder={t('features.audit.performedByPlaceholder')}
                            searchable
                            clearable
                            data={Array.from(lookups.users.values()).map((u) => ({
                                value: u.id,
                                label: `${u.firstName} ${u.lastName} (${u.email ?? u.id})`,
                            }))}
                            value={performedBy || null}
                            onChange={(v) => setPerformedBy(v ?? '')}
                        />
                    </Group>
                    <Group align="flex-end" grow>
                        <TextInput
                            label={t('common.actions.search')}
                            placeholder={t('features.audit.searchPlaceholder')}
                            value={searchQ}
                            onChange={(e) => setSearchQ(e.currentTarget.value)}
                        />
                        <Select
                            label={t('fields.organization')}
                            clearable
                            searchable
                            data={Array.from(lookups.organizations.values()).map((org) => ({
                                value: org.id,
                                label: org.name,
                            }))}
                            value={organizationId}
                            onChange={setOrganizationId}
                        />
                        <DatePickerInput
                            label={t('fields.from')}
                            value={from}
                            onChange={setFrom}
                            clearable
                        />
                        <DatePickerInput label={t('fields.to')} value={to} onChange={setTo} clearable />
                        <Button onClick={() => void refetch()} variant="light">
                            {t('common.actions.applyFilters')}
                        </Button>
                    </Group>
                </Stack>
            </DataPanel>
            <PaginatedTable
                rowKey={(r) => r.id}
                columns={[
                    {
                        key: 'at',
                        header: t('fields.when'),
                        render: (r) => (
                            <Text size="sm">
                                {r.createdAt ? new Date(r.createdAt).toLocaleString() : t('common.empty')}
                            </Text>
                        ),
                    },
                    {
                        key: 'type',
                        header: t('fields.entity'),
                        render: (r) => (
                            <Badge variant="outline" size="sm">
                                {entityTypeLabel(r.entityType)}
                            </Badge>
                        ),
                    },
                    {
                        key: 'eid',
                        header: t('fields.record'),
                        render: (r) => {
                            const title =
                                r.context?.summaryLabel ??
                                lookups.auditEntityLabel(r.entityType, r.entityId);
                            const path = entityDetailPath(r);
                            return (
                                <EntityRef
                                    title={title}
                                    id={r.entityId}
                                    href={path ?? undefined}
                                />
                            );
                        },
                    },
                    {
                        key: 'action',
                        header: t('fields.action'),
                        render: (r) => (
                            <Badge variant="light" size="sm">
                                {r.action}
                            </Badge>
                        ),
                    },
                    {
                        key: 'by',
                        header: t('fields.performer'),
                        render: (r) => {
                            const u = lookups.resolveUser(r.performedBy, r.performer ?? null);
                            return (
                                <EntityRef
                                    title={u.title}
                                    id={u.id}
                                    secondary={u.secondary}
                                    hideId={!r.performedBy}
                                />
                            );
                        },
                    },
                ]}
                rows={data?.data ?? []}
                total={data?.total ?? 0}
                page={page}
                pageSize={DEFAULT_PAGE_SIZE}
                onPageChange={setPage}
                isLoading={isLoading}
                onRowClick={(r) => setExpandedId(expandedId === r.id ? null : r.id)}
            />
            {expandedLog ? (
                <DataPanel>
                    <Stack gap="md">
                        <DetailFieldGrid>
                            <DetailField
                                label={t('fields.auditLogId')}
                                value={<CopyableId id={expandedLog.id} />}
                            />
                            <DetailField
                                label={t('fields.entityType')}
                                value={entityTypeLabel(expandedLog.entityType)}
                                copyValue={expandedLog.entityType}
                            />
                            <DetailField
                                label={t('fields.entityId')}
                                value={<CopyableId id={expandedLog.entityId} />}
                            />
                            <DetailField label={t('fields.action')} value={expandedLog.action} />
                            {expandedLog.performedBy ? (
                                <DetailField
                                    label={t('fields.performedByUserId')}
                                    value={<CopyableId id={expandedLog.performedBy} />}
                                />
                            ) : null}
                        </DetailFieldGrid>
                        <Accordion defaultValue="diff">
                            <Accordion.Item value="diff">
                                <Accordion.Control>{t('detail.changeDetails')}</Accordion.Control>
                                <Accordion.Panel>
                                    <JsonBlock value={expandedLog.diff ?? null} />
                                </Accordion.Panel>
                            </Accordion.Item>
                            {expandedLog.context ? (
                                <Accordion.Item value="context">
                                    <Accordion.Control>{t('detail.auditContext')}</Accordion.Control>
                                    <Accordion.Panel>
                                        <JsonBlock value={expandedLog.context} />
                                    </Accordion.Panel>
                                </Accordion.Item>
                            ) : null}
                        </Accordion>
                    </Stack>
                </DataPanel>
            ) : null}
        </Stack>
    );
}
